import React, { useState } from 'react';
import { Box, SectionHelper, Text, TextButton } from '@wix/design-system';
import { composer } from '../../../../component-decorators/composer';
import { biLogger } from '../../../../bi';
import {
  appMarketAppPageOneDependencyClick,
  appMarketAppPageOneDependencyImpression,
  appMarketClickOnCompleteSetup,
  appMarketToastClickOnCta,
  appMarketToastClosedX,
  appMarketToastPresented,
} from '@wix/bi-logger-market/v2';
import { isBundleAppAndInstalled } from '../../../../common/utils';
import type { MarketplaceStore } from '../../../../component-decorators/with-marketplace-store';
import type { InjectedExperimentsProps } from '@wix/wix-experiments-react';
import type { WithTranslation as WithTranslationProps } from 'react-i18next';
import { useConfigContext } from '../../../../config-context';
import { isAppPendingConfiguration } from '../../../../common/is-app-pending-configuration';
import type { IAppDependenciesProps } from '../solution-page';
import { config } from '../../../../config';
import { Path } from '../../../../enums/marketplace-enums';
import { getReferralInfo } from '../../../../common/bi-events';
import { httpClient } from '../../../../http-client';

interface SolutionHelperProps
  extends Partial<InjectedExperimentsProps>,
    Partial<WithTranslationProps> {
  solutionId: string;
  appName: string;
  marketplaceStore?: MarketplaceStore;
  appDependencies: IAppDependenciesProps;
  isNotAvailableToInstall?: boolean;
}

export const SolutionHelper = composer()
  .withMarketplaceStore()
  .withTranslation()
  .withExperiments()
  .compose(
    ({
      solutionId,
      appName,
      marketplaceStore,
      experiments,
      appDependencies,
      t,
      isNotAvailableToInstall,
    }: SolutionHelperProps) => {
      const HOTEL_RUNNER_APP_ID = '826670ba-3a6b-4157-ac72-7c4fca9ce220';
      const HOTEL_RUNNER_STAGING_APP_ID =
        'ee4f5349-bf6f-46a8-aaf6-8cba4f9726ff';
      const isHotels =
        solutionId === HOTEL_RUNNER_APP_ID ||
        solutionId === HOTEL_RUNNER_STAGING_APP_ID;
      const isHotelsInstalled = !!marketplaceStore.installedApps?.find(
        (installedApp) =>
          installedApp.appId === HOTEL_RUNNER_APP_ID ||
          installedApp.appId === HOTEL_RUNNER_STAGING_APP_ID,
      );
      const showHotelsHelper =
        isHotels &&
        !isHotelsInstalled &&
        experiments.enabled('specs.marketplace.hotelsBetaBadge');
      const showHotelsHelperAfterInstall =
        isHotels &&
        isHotelsInstalled &&
        experiments.enabled('specs.marketplace.hotelsBetaBadge');
      const showAppDependenciesBanner =
        isNotAvailableToInstall ||
        (appDependencies.showAppDependencyHelper !== undefined &&
          appDependencies.showAppDependencyHelper !== 'None');
      const showBundleHelper = isBundleAppAndInstalled(
        solutionId,
        marketplaceStore.bundleApps,
      );
      const installedApp = marketplaceStore.installedApps?.find(
        (installedAppVar) => installedAppVar.appId === solutionId,
      );
      const shouldDisplayIncompleteSetupNotice =
        isAppPendingConfiguration(installedApp) && !isHotels;
      let appDependencyHelperTexts;
      function getTexts() {
        if (isNotAvailableToInstall) {
          return {
            title: 'solution.page.isNotCompatible.title',
            subtitle: 'solution.page.isNotCompatible.subtitle',
            cta: undefined,
          };
        }
        if (appDependencies?.needToInstallApps?.length > 1) {
          return {
            title: 'solution.page.appDependenciesNotInstalled.title',
            subtitle: 'solution.page.appDependenciesNotInstalled.subtitle',
            cta: undefined,
          };
        }

        if (
          appDependencies?.needToInstallApps?.length === 1 &&
          appDependencies.showAppDependencyHelper === 'App-Req-Pending'
        ) {
          return {
            title: 'solution.page.appDependencyPending.title',
            subtitle: 'solution.page.appDependencyPending.subtitle',
            cta: 'solution.page.appDependencyPending.cta',
          };
        }
        if (
          appDependencies?.needToInstallApps?.length === 1 &&
          appDependencies.showAppDependencyHelper === 'App-Req-Installation'
        ) {
          return {
            title: 'solution.page.appDependencyNotInstalled.title',
            subtitle: 'solution.page.appDependencyNotInstalled.subtitle',
            cta: 'solution.page.appDependencyNotInstalled.cta',
          };
        }
      }
      if (showAppDependenciesBanner) {
        appDependencyHelperTexts = getTexts();
        biLogger.report(
          appMarketAppPageOneDependencyImpression({
            app_id: solutionId,
            dependency_type: 'app',
            app_dependency: appDependencies?.needToInstallApps
              ?.map((x) => x.id)
              .toString(),
            type:
              appDependencies.showAppDependencyHelper === 'App-Req-Pending'
                ? 'pending'
                : appDependencies.needToInstallApps.length > 1
                ? 'multiple'
                : 'one',
          }),
        );
      }

      const { openPackagePicker, isBusinessFirstSite } = useConfigContext();

      if (showBundleHelper) {
        biLogger.report(
          appMarketToastPresented({
            action_cta: 'open_package_picker',
            app_id: solutionId,
          }),
        );
      }
      const currentApp = marketplaceStore.installedApps?.find(
        (installedAppVar) => installedAppVar.appId === solutionId,
      );

      const [isClosed, setIsClosed] = useState(false);
      const editorTypeForTitle =
        config.editorType === 'EDITORX'
          ? t('modals-notSupportedInEditorX')
          : config.editorType === 'STUDIO'
          ? t('modals-notSupportedStudio')
          : '';
      const editorTypeForSubTitle =
        config.editorType === 'EDITORX'
          ? t('modals-notSupportedInEditorX')
          : config.editorType === 'STUDIO'
          ? t('solution.page.helper.studio')
          : '';

      return (
        !isClosed && (
          <>
            {showAppDependenciesBanner && (
              <Box marginTop="12px">
                <SectionHelper
                  actionText={t(appDependencyHelperTexts?.cta, {
                    appRequirement:
                      appDependencies?.needToInstallApps?.[0]?.name,
                  })}
                  appearance="danger"
                  onAction={async () => {
                    biLogger.report(
                      appMarketAppPageOneDependencyClick({
                        app_id: solutionId,
                        dependency_type: 'app',
                        click_type:
                          appDependencies.showAppDependencyHelper ===
                          'App-Req-Installation'
                            ? 'app'
                            : 'go to editor',
                        app_dependency: appDependencies?.needToInstallApps
                          ?.map((x) => x.id)
                          .toString(),
                        type:
                          appDependencies.showAppDependencyHelper ===
                          'App-Req-Pending'
                            ? 'pending'
                            : appDependencies.needToInstallApps.length > 1
                            ? 'multiple'
                            : 'one',
                      }),
                    );
                    if (appDependencies?.needToInstallApps?.length === 1) {
                      if (
                        appDependencies.showAppDependencyHelper ===
                        'App-Req-Installation'
                      ) {
                        config.goto({
                          path: Path.WEB_SOLUTION,
                          slug: appDependencies?.needToInstallApps?.[0]?.slug,
                          referral: `app-dependency-${solutionId}`,
                        });
                      } else {
                        if (isBusinessFirstSite) {
                          window.open(
                            `https://manage.wix.com/site-creation-split/?metaSiteId=${marketplaceStore.metaSiteId}`,
                          );
                          return;
                        }

                        const appMarketParams = {
                          data: JSON.stringify({
                            appDefId:
                              appDependencies?.needToInstallApps?.[0]?.id,
                            version: marketplaceStore?.route.version,
                          }),
                        };
                        const { data: signedAppMarketParams } =
                          await httpClient.post<any>(
                            '/_serverless/app-market-json-web-token/json-web-token',
                            { data: appMarketParams },
                          );

                        const url = new URL(
                          `${window.location.origin}${config.editorUrl}`,
                        );
                        url.searchParams.append(
                          'appMarketParams',
                          signedAppMarketParams,
                        );
                        url.searchParams.append('openPanel', 'appMarket');
                        url.searchParams.append('preferAppManager', 'true');
                        window.open(url.toString());
                      }
                    }
                  }}
                  fullWidth={true}
                  title={
                    <Box direction="vertical">
                      <Text size="small" weight="bold">
                        {t(appDependencyHelperTexts?.title, {
                          appRequirement:
                            appDependencies?.needToInstallApps?.[0]?.name,
                          appDependency: appName,
                          appName,
                          editorType: editorTypeForTitle,
                        })}
                      </Text>
                      {appDependencies?.needToInstallApps?.length > 1 && (
                        <Box marginTop="6px" direction="horizontal">
                          {appDependencies?.needToInstallApps?.map(
                            (app, index) => (
                              <>
                                <TextButton
                                  size="small"
                                  underline="always"
                                  skin="dark"
                                  onClick={() => {
                                    biLogger.report(
                                      appMarketAppPageOneDependencyClick({
                                        app_id: solutionId,
                                        dependency_type: 'app',
                                        click_type: '',
                                        app_dependency: app.id,
                                        // type
                                      }),
                                    );
                                    config.goto({
                                      path: Path.WEB_SOLUTION,
                                      slug: app.slug,
                                      referral: `app-dependency-${solutionId}`,
                                    });
                                  }}
                                >
                                  {app.name}
                                </TextButton>
                                {index !==
                                  appDependencies?.needToInstallApps.length -
                                    1 && (
                                  <Box marginLeft="3px" marginRight="3px">
                                    {' '}
                                    <Text> {' / '} </Text>{' '}
                                  </Box>
                                )}
                              </>
                            ),
                          )}
                        </Box>
                      )}
                    </Box>
                  }
                >
                  <Text size="small" weight="thin">
                    {t(appDependencyHelperTexts?.subtitle, {
                      appRequirement:
                        appDependencies?.needToInstallApps?.[0]?.name,
                      appDependency: appName,
                      editorType: editorTypeForSubTitle,
                    })}
                  </Text>
                </SectionHelper>
              </Box>
            )}
            {showBundleHelper && (
              <Box marginTop="12px">
                <SectionHelper
                  actionText={t('solution.page.bundleHelper.cta')}
                  appearance="standard"
                  onAction={() => {
                    biLogger.report(
                      appMarketToastClickOnCta({
                        action_cta: 'open_package_picker',
                        app_id: solutionId,
                      }),
                    );
                    openPackagePicker(
                      marketplaceStore.installedApps &&
                        marketplaceStore.installedApps?.find(
                          (app) => app.appId === solutionId,
                        ),
                      'solution_page_bundle_apps',
                      true,
                    );
                  }}
                  onClose={() => {
                    biLogger.report(
                      appMarketToastClosedX({
                        action_cta: 'open_package_picker',
                        app_id: solutionId,
                      }),
                    );
                    setIsClosed(true);
                  }}
                  fullWidth={true}
                  title={
                    <Text size="small" weight="bold">
                      {t('solution.page.bundleHelper.title')}
                    </Text>
                  }
                >
                  <Text size="small">
                    {t('solution.page.bundleHelper.subtitle', { appName })}
                  </Text>
                  <TextButton
                    as="a"
                    dataHook="learn-more-bundle"
                    size="small"
                    target="_blank"
                    underline="always"
                    skin="dark"
                    href={t('solution.page.bundleHelper.subtitle.article.url')}
                  >
                    {t('solution.page.bundleHelper.subtitle.article.anchor')}
                  </TextButton>
                </SectionHelper>
              </Box>
            )}
            {showHotelsHelper && (
              <Box marginTop="12px">
                <SectionHelper
                  appearance="preview"
                  onClose={() => {
                    setIsClosed(false);
                  }}
                  fullWidth={true}
                >
                  <Text size="small">
                    {t('solution.page.HotelsHelper.title')}
                  </Text>
                </SectionHelper>
              </Box>
            )}
            {showHotelsHelperAfterInstall && (
              <Box marginTop="12px">
                <SectionHelper
                  actionText={t('solution.page.HotelsHelperInstalled.cta')}
                  appearance="preview"
                  onAction={() => {
                    window.open(t('solution.page.HotelsHelperInstalled.link'));
                  }}
                  onClose={() => {
                    setIsClosed(true);
                  }}
                  fullWidth={true}
                  title={
                    <Text size="small" weight="bold">
                      {t('solution.page.HotelsHelperInstalled.title')}
                    </Text>
                  }
                >
                  <Text size="small">
                    {t('solution.page.HotelsHelperInstalled.subtitle', {
                      appName,
                    })}
                  </Text>
                </SectionHelper>
              </Box>
            )}
            {shouldDisplayIncompleteSetupNotice && (
              <Box marginTop="12px">
                <SectionHelper
                  appearance="warning"
                  title={t('solution.page.incompleteSetupHelper.title')}
                  actionText={t(
                    'solution.page.incompleteSetupHelper.actionButtonText',
                  )}
                  onAction={() => {
                    biLogger.report(
                      appMarketClickOnCompleteSetup({
                        app_id: solutionId,
                        referral_info: getReferralInfo(
                          marketplaceStore.route?.referral,
                          marketplaceStore.prevRoute,
                        ),
                      }),
                    );
                    config.installApp({
                      appDefId: solutionId,
                      openMethod: currentApp?.appInfo?.openConsentIn,
                      version: marketplaceStore?.route.version,
                    });
                  }}
                >
                  {t('solution.page.incompleteSetupHelper.content')}
                </SectionHelper>
              </Box>
            )}
          </>
        )
      );
    },
  );
